import {
  Binary,
  Braces,
  Clock,
  Code2,
  FileArchive,
  FileCode2,
  FileText,
  Globe2,
  Hash,
  ImageIcon,
  KeyRound,
  Link2,
  Network,
  Palette,
  Rows3,
  Search,
  ShieldCheck,
  Sparkles,
  SplitSquareHorizontal,
  Table2,
  Terminal,
  TextCursorInput,
  Type,
  WandSparkles,
} from "lucide-react";
import type {
  ToolCategory,
  ToolCategoryId,
  ToolRegistryItem,
} from "@/types/tools";
import { getToolSeoContent } from "./seo-content";

export const toolCategories: ToolCategory[] = [
  {
    id: "developer",
    name: "Developer tools",
    description: "Format, parse, encode, decode, and generate common dev assets.",
    accent: "bg-cyan-500",
    icon: Code2,
  },
  {
    id: "text",
    name: "Text tools",
    description: "Clean up text, compare changes, and convert naming formats.",
    accent: "bg-emerald-500",
    icon: Type,
  },
  {
    id: "web-seo",
    name: "Web/SEO tools",
    description: "Build metadata, UTM links, previews, and crawl directives.",
    accent: "bg-amber-500",
    icon: Globe2,
  },
  {
    id: "image",
    name: "Image tools",
    description: "Local-first image cleanup and resizing workflows.",
    accent: "bg-rose-500",
    icon: ImageIcon,
  },
  {
    id: "pdf",
    name: "PDF tools",
    description: "Merge, split, and optimize PDF files locally in the browser.",
    accent: "bg-violet-500",
    icon: FileText,
  },
  {
    id: "network",
    name: "Network tools",
    description: "Check public HTTP details that need a small server request.",
    accent: "bg-sky-500",
    icon: Network,
  },
];

export const tools: ToolRegistryItem[] = [
  {
    id: "json-formatter",
    name: "JSON Formatter and Validator",
    description: "Validate raw JSON and format it with readable indentation.",
    seo: {
      title: "Local JSON Formatter - No Upload",
      description:
        "Format, validate, and pretty-print JSON locally in your browser. No uploads, no account, and safer for sensitive API responses, logs, and webhook payloads.",
      keywords: [
        "local JSON formatter",
        "json formatter no upload",
        "offline json formatter",
        "json validator",
        "json pretty print",
        "format JSON in browser",
      ],
    },
    category: "developer",
    tags: ["json", "formatter", "validator", "pretty print"],
    route: "/tools/json-formatter",
    localOnly: true,
    status: "available",
    popular: true,
    relatedIds: ["json-minifier", "yaml-to-json", "url-parser"],
  },
  {
    id: "json-minifier",
    name: "JSON Minifier",
    description: "Validate JSON and remove whitespace for compact output.",
    category: "developer",
    tags: ["json", "minify", "compress"],
    route: "/tools/json-minifier",
    localOnly: true,
    status: "available",
    relatedIds: ["json-formatter", "yaml-to-json"],
  },
  {
    id: "yaml-to-json",
    name: "YAML to JSON",
    description: "Convert YAML documents into formatted JSON safely in the browser.",
    category: "developer",
    tags: ["yaml", "json", "convert"],
    route: "/tools/yaml-to-json",
    localOnly: true,
    status: "available",
    relatedIds: ["json-formatter", "json-minifier"],
  },
  {
    id: "base64",
    name: "Base64 Encode/Decode",
    description: "Encode Unicode text to Base64 or decode Base64 back to text.",
    seo: {
      title: "Local Base64 Encoder and Decoder",
      description:
        "Encode text to Base64 or decode Base64 locally in your browser. No uploads, no account, and useful for API payloads, JWT segments, and logs.",
      keywords: [
        "base64 encode decode",
        "base64 decoder no upload",
        "local base64 decoder",
        "base64 encoder",
        "decode base64 in browser",
      ],
    },
    category: "developer",
    tags: ["base64", "encode", "decode", "unicode"],
    route: "/tools/base64",
    localOnly: true,
    status: "available",
    popular: true,
    relatedIds: ["html-escape", "jwt-decoder", "hash-generator"],
  },
  {
    id: "html-escape",
    name: "HTML Escape/Unescape",
    description: "Escape HTML special characters or unescape common HTML entities.",
    category: "developer",
    tags: ["html", "entities", "escape", "unescape"],
    route: "/tools/html-escape",
    localOnly: true,
    status: "available",
    relatedIds: ["base64", "url-parser", "json-formatter"],
  },
  {
    id: "jwt-decoder",
    name: "JWT Decoder",
    description: "Decode JWT headers and payloads locally without verifying signatures.",
    seo: {
      title: "Local JWT Decoder - No Upload",
      description:
        "Decode JWT headers and payloads locally in your browser. Inspect exp, iat, nbf, iss, aud, sub, and other claims without uploading the token.",
      keywords: [
        "local JWT decoder",
        "decode JWT locally",
        "JWT decoder no upload",
        "safe JWT decoder",
        "JWT exp checker",
        "JWT claims viewer",
      ],
    },
    category: "developer",
    tags: ["jwt", "token", "decode", "security"],
    route: "/tools/jwt-decoder",
    localOnly: true,
    status: "available",
    popular: true,
    relatedIds: ["base64", "json-formatter", "hash-generator"],
  },
  {
    id: "uuid-generator",
    name: "UUID Generator",
    description: "Generate one or many v4 UUIDs for development and testing.",
    category: "developer",
    tags: ["uuid", "generator", "v4", "id"],
    route: "/tools/uuid-generator",
    localOnly: true,
    status: "available",
    relatedIds: ["hash-generator"],
  },
  {
    id: "hash-generator",
    name: "Hash Generator",
    description: "Generate SHA-1, SHA-256, and SHA-512 hashes with Web Crypto.",
    category: "developer",
    tags: ["hash", "sha", "crypto", "checksum"],
    route: "/tools/hash-generator",
    localOnly: true,
    status: "available",
    popular: true,
    relatedIds: ["base64", "jwt-decoder", "uuid-generator"],
  },

  {
    id: "password-generator",
    name: "Password Generator",
    description: "Create strong random passwords with custom character rules.",
    category: "developer",
    tags: ["password", "security", "random", "generator"],
    route: "/tools/password-generator",
    localOnly: true,
    status: "available",
    popular: true,
    relatedIds: ["hash-generator", "uuid-generator"],
  },
  {
    id: "timestamp-converter",
    name: "Timestamp Converter",
    description: "Convert Unix timestamps, ISO strings, and local dates without leaving the browser.",
    category: "developer",
    tags: ["timestamp", "unix", "epoch", "date", "time"],
    route: "/tools/timestamp-converter",
    localOnly: true,
    status: "available",
    relatedIds: ["url-parser", "uuid-generator", "hash-generator"],
  },
  {
    id: "url-encoder",
    name: "URL Encoder/Decoder",
    description: "Percent-encode URL components or decode percent-encoded URL text.",
    seo: {
      title: "URL Encoder/Decoder (Percent Encode Locally)",
      description: "Encode query params and decode percent-encoded strings locally with no upload.",
      keywords: ["url encoder offline", "encode query params", "percent decode tool"],
    },
    featureList: [
      "Percent-encode reserved characters for safe URLs.",
      "Decode URL-encoded values for easier debugging.",
      "Process links locally in-browser.",
    ],
    category: "developer",
    tags: ["url", "encode", "decode", "percent"],
    route: "/tools/url-encoder",
    localOnly: true,
    status: "available",
    relatedIds: ["url-parser", "utm-builder", "base64"],
  },
  {
    id: "color-converter",
    name: "CSS Color Converter",
    description: "Convert HEX, RGB, and RGBA colors into CSS-ready HEX and HSL variants.",
    category: "developer",
    tags: ["color", "css", "hex", "rgb", "hsl"],
    route: "/tools/color-converter",
    localOnly: true,
    status: "available",
    relatedIds: ["json-formatter", "case-converter", "image-resize"],
  },
  {
    id: "csv-to-markdown",
    name: "CSV to Markdown Table",
    description: "Turn simple CSV rows into a markdown table for docs and README files.",
    category: "text",
    tags: ["csv", "markdown", "table", "docs"],
    route: "/tools/csv-to-markdown",
    localOnly: true,
    status: "available",
    relatedIds: ["text-diff", "case-converter"],
  },
  {
    id: "url-parser",
    name: "URL Parser",
    description: "Break URLs into protocol, hostname, path, query parameters, and hash.",
    category: "developer",
    tags: ["url", "parser", "query", "params"],
    route: "/tools/url-parser",
    localOnly: true,
    status: "available",
    relatedIds: ["url-encoder", "utm-builder", "json-formatter"],
  },
  {
    id: "curl-translator",
    name: "cURL Translator",
    description: "Explain, inspect, and convert cURL commands into Fetch, Axios, Python, HTTPie, and cleaner cURL.",
    seo: {
      title: "cURL Translator and Explainer",
      description:
        "Translate cURL locally into Fetch, Axios, Python requests, HTTPie, and request details with headers, body, auth, cookies, warnings, and redaction.",
      keywords: [
        "curl translator",
        "curl to fetch",
        "curl to python requests",
        "curl explainer",
        "curl to axios",
        "curl parser",
        "explain curl command",
      ],
    },
    llmSummary:
      "Browser-local cURL workbench that parses common cURL commands, explains method URL headers auth cookies body and flags, detects likely secrets and risky options, and generates Fetch Axios Python requests HTTPie and normalized cURL snippets.",
    featureList: [
      "Parses common single-line and multiline cURL commands",
      "Explains request method, URL, query params, headers, body, auth, cookies, and flags",
      "Generates Fetch, Axios, Python requests, HTTPie, and normalized cURL snippets",
      "Redacts likely secrets and warns about insecure TLS, file paths, duplicate headers, and unsupported flags",
    ],
    category: "developer",
    tags: ["curl", "api", "fetch", "axios", "python", "http"],
    route: "/tools/curl-translator",
    localOnly: true,
    status: "available",
    popular: true,
    relatedIds: ["url-parser", "json-formatter", "headers-checker", "jwt-decoder"],
  },
  {
    id: "text-diff",
    name: "Text Diff",
    description: "Compare two blocks of text with a simple line-by-line diff.",
    category: "text",
    tags: ["diff", "compare", "text", "lines"],
    route: "/tools/text-diff",
    localOnly: true,
    status: "available",
    relatedIds: ["case-converter", "slug-generator"],
  },
  {
    id: "text-statistics",
    name: "Text Statistics",
    description: "Count words, characters, lines, paragraphs, and reading time locally.",
    category: "text",
    tags: ["word count", "characters", "lines", "reading time"],
    route: "/tools/text-statistics",
    localOnly: true,
    status: "available",
    relatedIds: ["text-diff", "case-converter", "slug-generator"],
  },
  {
    id: "slug-generator",
    name: "Slug Generator",
    description: "Create URL-safe slugs with hyphen or underscore separators.",
    category: "text",
    tags: ["slug", "url", "seo", "diacritics"],
    route: "/tools/slug-generator",
    localOnly: true,
    status: "available",
    popular: true,
    relatedIds: ["case-converter", "utm-builder"],
  },
  {
    id: "case-converter",
    name: "Case Converter",
    description: "Convert text into camelCase, PascalCase, snake_case, and more.",
    category: "text",
    tags: ["case", "camel", "snake", "kebab", "title"],
    route: "/tools/case-converter",
    localOnly: true,
    status: "available",
    relatedIds: ["slug-generator", "text-diff"],
  },
  {
    id: "utm-builder",
    name: "UTM Builder",
    description: "Generate campaign URLs with validated UTM parameters.",
    category: "web-seo",
    tags: ["utm", "marketing", "campaign", "url"],
    route: "/tools/utm-builder",
    localOnly: true,
    status: "available",
    popular: true,
    relatedIds: ["url-parser", "slug-generator"],
  },
  {
    id: "meta-tag-generator",
    name: "Meta Tag Generator",
    description: "Generate title, description, canonical, and OpenGraph meta tags.",
    category: "web-seo",
    tags: ["meta", "html", "opengraph", "seo"],
    route: "/tools/meta-tag-generator",
    localOnly: true,
    status: "available",
    relatedIds: ["opengraph-preview", "utm-builder", "robots-generator"],
  },
  {
    id: "opengraph-preview",
    name: "OpenGraph Preview",
    description: "Preview social cards and generate matching OpenGraph tags.",
    category: "web-seo",
    tags: ["opengraph", "social", "preview", "meta"],
    route: "/tools/opengraph-preview",
    localOnly: true,
    status: "available",
    relatedIds: ["meta-tag-generator", "utm-builder"],
  },
  {
    id: "robots-generator",
    name: "robots.txt Generator",
    description: "Create common robots.txt rules for crawl access and blocked paths.",
    category: "web-seo",
    tags: ["robots", "crawler", "seo", "txt"],
    route: "/tools/robots-generator",
    localOnly: true,
    status: "available",
    relatedIds: ["meta-tag-generator", "utm-builder"],
  },
  {
    id: "image-metadata-stripper",
    name: "Image Metadata Stripper",
    description: "Remove EXIF and editor metadata by re-encoding images locally.",
    category: "image",
    tags: ["image", "metadata", "exif", "privacy"],
    route: "/tools/image-metadata-stripper",
    localOnly: true,
    status: "available",
    relatedIds: ["image-resize", "svg-to-image"],
  },
  {
    id: "image-resize",
    name: "Image Resize",
    description: "Resize images locally in the browser with canvas controls.",
    category: "image",
    tags: ["image", "resize", "canvas", "quality"],
    route: "/tools/image-resize",
    localOnly: true,
    status: "available",
    relatedIds: ["svg-to-image", "image-metadata-stripper"],
  },
  {
    id: "svg-to-image",
    name: "SVG to Image Converter",
    description: "Convert SVG markup or files into PNG, JPEG, or WebP images locally.",
    seo: {
      title: "SVG to PNG/JPEG/WebP Converter",
      description:
        "Convert SVG files or pasted SVG markup to PNG, JPEG, or WebP locally in your browser. Preview, resize, and download without uploading the SVG.",
      keywords: [
        "svg to image",
        "svg to png",
        "convert svg to png locally",
        "svg to jpg",
        "svg to webp",
        "local svg converter",
      ],
    },
    llmSummary:
      "Browser-local SVG converter that accepts pasted SVG markup or SVG files, blocks external references, rasterizes to canvas, and exports PNG, JPEG, or WebP with selectable dimensions.",
    featureList: [
      "Convert pasted SVG markup or uploaded SVG files",
      "Paste CSS rules for SVGs that rely on class names",
      "Export PNG, JPEG, or WebP images",
      "Set output dimensions before download",
      "Block external SVG references for local-only conversion",
    ],
    category: "image",
    tags: ["svg", "png", "jpeg", "webp", "converter", "image"],
    route: "/tools/svg-to-image",
    localOnly: true,
    status: "available",
    relatedIds: ["image-resize", "image-metadata-stripper", "color-converter"],
  },
  {
    id: "pdf-merge",
    name: "PDF Merge",
    description: "Combine multiple PDF files into one local browser-generated document.",
    category: "pdf",
    tags: ["pdf", "merge", "documents"],
    route: "/tools/pdf-merge",
    localOnly: true,
    status: "available",
    relatedIds: ["pdf-split", "pdf-compress"],
  },
  {
    id: "pdf-split",
    name: "PDF Split",
    description: "Extract selected page ranges from a PDF into a new document.",
    category: "pdf",
    tags: ["pdf", "split", "pages"],
    route: "/tools/pdf-split",
    localOnly: true,
    status: "available",
    relatedIds: ["pdf-merge", "pdf-compress"],
  },
  {
    id: "pdf-compress",
    name: "PDF Compress",
    description: "Optimize a PDF with a lossless local rewrite and document metadata cleanup.",
    category: "pdf",
    tags: ["pdf", "compress", "optimize"],
    route: "/tools/pdf-compress",
    localOnly: true,
    status: "available",
    relatedIds: ["pdf-merge", "pdf-split"],
  },
  {
    id: "headers-checker",
    name: "Headers Checker",
    description: "Inspect status and response headers for public HTTP and HTTPS URLs.",
    category: "network",
    tags: ["headers", "network", "http", "security"],
    route: "/tools/headers-checker",
    localOnly: false,
    status: "available",
    relatedIds: ["redirect-chain-checker", "security-headers-auditor", "url-parser"],
  },
  {
    id: "redirect-chain-checker",
    name: "Redirect Tracker",
    description:
      "Track public URL redirects with hop diagnostics, final URL checks, status codes, timing, headers, and SEO cleanup insights.",
    seo: {
      title: "Redirect Tracker - Check Chains & Final URLs",
      description:
        "Redirect tracker for public URLs. Trace 301, 302, 307, and 308 chains, final URLs, HTTPS changes, headers, timing, loops, and SEO issues.",
      keywords: [
        "redirect tracker",
        "free redirect tracker",
        "online redirect tracker",
        "redirect tracker tool",
        "redirect chain checker",
        "URL redirect checker",
        "URL redirect tracker",
        "301 redirect checker",
        "302 redirect checker",
        "HTTP redirect tracker",
        "SEO redirect audit",
        "redirect loop checker",
        "final URL checker",
      ],
    },
    llmSummary:
      "Server-assisted public URL tracker that follows HTTP redirects for single URLs or small batches, reports each hop with status, Location, timing, headers, URL deltas, request profile, and grades SEO/network redirect health. It detects loops, extra hops, temporary redirects, HTTPS downgrades, final status errors, query changes, cached temporary redirects, cookie-setting redirects, and slow chains.",
    featureList: [
      "Redirect tracker for one URL or a batch of URLs with request profile and timeout controls",
      "Shows each redirect hop with HTTP status, Location target, resolved destination, URL deltas, headers, cache signals, cookies, and timing",
      "Grades redirect health for SEO, crawler efficiency, HTTPS safety, query preservation, cacheability, and final status",
      "Detects redirect loops, too many redirects, missing Location headers, blocked private redirects, temporary redirect chains, HTTPS downgrades, cached temporary redirects, and split canonicalization",
      "Provides copyable reports, CSV exports, JSON traces, and local recent trace history",
    ],
    alternateNames: [
      "URL Redirect Tracker",
      "HTTP Redirect Tracker",
      "Redirect Chain Checker",
      "301 Redirect Checker",
      "Final URL Checker",
    ],
    faq: [
      {
        question: "What does the Redirect Tracker inspect?",
        answer:
          "The Redirect Tracker sends a server-side request to a public HTTP or HTTPS URL, follows redirect responses manually, and reports each hop, status code, Location target, final URL, timing, headers, and SEO-oriented findings.",
      },
      {
        question: "Can I track multiple redirects at once?",
        answer:
          "Yes. Batch mode lets you run several public URLs through the redirect tracker and compare grades, final URLs, redirect counts, timing, and top cleanup signals.",
      },
      {
        question: "Why do redirect chains matter for SEO?",
        answer:
          "Long or temporary redirect chains can waste crawl budget, add latency, weaken canonical signals, and hide final destination errors. One direct permanent redirect is usually cleaner for stable migrations.",
      },
      {
        question: "Does the Redirect Tracker access private networks?",
        answer:
          "No. Local, private, reserved, and credentialed URLs are blocked before the server fetch, and redirected destinations are checked again before following.",
      },
    ],
    category: "network",
    tags: [
      "redirect",
      "redirect tracker",
      "redirect chain",
      "301",
      "302",
      "http status",
      "final url",
      "seo",
      "crawler",
      "timing",
      "network",
    ],
    route: "/tools/redirect-chain-checker",
    localOnly: false,
    status: "available",
    popular: true,
    relatedIds: ["headers-checker", "security-headers-auditor", "url-parser", "utm-builder"],
  },
  {
    id: "security-headers-auditor",
    name: "Security Headers Auditor",
    description:
      "Audit CSP, HSTS, clickjacking, MIME sniffing, referrer, permissions, CORS, and cross-origin isolation headers for public URLs.",
    seo: {
      title: "Security Headers Auditor and CSP Checker",
      description:
        "Free security headers auditor for CSP, HSTS, X-Frame-Options, Referrer-Policy, Permissions-Policy, CORS, COOP, COEP, and CORP on public URLs.",
      keywords: [
        "security headers checker",
        "CSP checker",
        "HSTS checker",
        "HTTP security header scanner",
        "Referrer-Policy checker",
        "Permissions-Policy checker",
        "COOP COEP CORP checker",
      ],
    },
    llmSummary:
      "Server-assisted public URL audit that grades HTTP security headers, explains missing or weak CSP/HSTS/clickjacking/privacy/isolation controls, and generates recommended baseline header snippets.",
    featureList: [
      "Grades public HTTP security header posture on a 100-point scale",
      "Audits Content-Security-Policy, HSTS, X-Content-Type-Options, frame-ancestors, X-Frame-Options, Referrer-Policy, Permissions-Policy, COOP, COEP, CORP, CORS, and disclosure headers",
      "Follows safe public redirects while blocking private, local, and reserved hosts",
      "Generates copyable reports plus baseline Nginx and Next.js header snippets",
    ],
    faq: [
      {
        question: "Does the Security Headers Auditor upload private data?",
        answer:
          "No page content, credentials, tokens, or files are uploaded. The server-assisted audit receives only the public URL entered and fetches its public response headers.",
      },
      {
        question: "Can this replace a penetration test?",
        answer:
          "No. It checks HTTP response header posture only. Use it as a fast hardening checklist alongside application testing, TLS checks, dependency review, and manual security review.",
      },
      {
        question: "Why can a site with headers still receive warnings?",
        answer:
          "Some headers are present but weak, overly broad, deprecated, report-only, or missing important directives such as CSP frame-ancestors, object-src, base-uri, or HSTS includeSubDomains.",
      },
    ],
    category: "network",
    tags: [
      "security headers",
      "csp",
      "hsts",
      "x-frame-options",
      "referrer-policy",
      "permissions-policy",
      "cors",
      "headers",
      "seo",
      "llm",
    ],
    route: "/tools/security-headers-auditor",
    localOnly: false,
    status: "available",
    popular: true,
    relatedIds: ["headers-checker", "redirect-chain-checker", "url-parser", "meta-tag-generator"],
  },
];

export const toolIcons: Record<string, typeof Code2> = {
  "json-formatter": Braces,
  "json-minifier": Rows3,
  "yaml-to-json": FileCode2,
  base64: Binary,
  "html-escape": FileCode2,
  "jwt-decoder": KeyRound,
  "uuid-generator": Sparkles,
  "hash-generator": Hash,
  "password-generator": WandSparkles,
  "timestamp-converter": Clock,
  "url-encoder": Link2,
  "url-parser": Link2,
  "curl-translator": Terminal,
  "color-converter": Palette,
  "csv-to-markdown": Table2,
  "text-diff": SplitSquareHorizontal,
  "text-statistics": Rows3,
  "slug-generator": Link2,
  "case-converter": TextCursorInput,
  "utm-builder": Search,
  "meta-tag-generator": FileCode2,
  "opengraph-preview": Globe2,
  "robots-generator": ShieldCheck,
  "image-metadata-stripper": ImageIcon,
  "image-resize": ImageIcon,
  "svg-to-image": ImageIcon,
  "pdf-merge": FileArchive,
  "pdf-split": FileArchive,
  "pdf-compress": FileArchive,
  "headers-checker": Network,
  "redirect-chain-checker": Link2,
  "security-headers-auditor": ShieldCheck,
};

export function getToolById(id: string) {
  return tools.find((tool) => tool.id === id);
}

export function getCategoryById(categoryId: ToolCategoryId) {
  return toolCategories.find((category) => category.id === categoryId);
}

export function getToolsByCategory(categoryId: ToolCategoryId) {
  return tools.filter((tool) => tool.category === categoryId);
}

export function getPopularTools() {
  return tools.filter((tool) => tool.popular);
}

export function getRelatedTools(tool: ToolRegistryItem, limit = 4) {
  const explicit = (tool.relatedIds ?? [])
    .map((id) => getToolById(id))
    .filter((item): item is ToolRegistryItem => Boolean(item));

  const sameCategory = tools.filter(
    (item) => item.category === tool.category && item.id !== tool.id,
  );

  return [...explicit, ...sameCategory]
    .filter(
      (item, index, list) => list.findIndex((candidate) => candidate.id === item.id) === index,
    )
    .slice(0, limit);
}

function normalizeSearchText(value: string) {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
}

export function searchTools(query: string, source: ToolRegistryItem[] = tools) {
  const normalized = normalizeSearchText(query);

  if (!normalized) {
    return source;
  }

  return source.filter((tool) => {
    const categoryName = getCategoryById(tool.category)?.name ?? "";
    const seoContent = getToolSeoContent(tool.id);
    const haystack = [
      tool.name,
      tool.description,
      tool.seo?.title ?? "",
      tool.seo?.description ?? "",
      tool.llmSummary ?? "",
      tool.category,
      categoryName,
      ...tool.tags,
      ...(tool.seo?.keywords ?? []),
      ...(tool.featureList ?? []),
      seoContent?.h1 ?? "",
      seoContent?.intro ?? "",
      seoContent?.privacy ?? "",
      ...(seoContent?.useCases ?? []),
      ...(seoContent?.howItWorks ?? []),
      ...(seoContent?.commonMistakes ?? []),
      ...(seoContent?.faqs?.flatMap((faq) => [faq.question, faq.answer]) ?? []),
    ]
      .join(" ");

    return normalizeSearchText(haystack).includes(normalized);
  });
}
